import React, {
  FunctionComponent,
  useState,
  useEffect,
  SyntheticEvent,
  useRef,
} from 'react';
import { ApolloError } from 'apollo-boost';
import { pathOr, isEmpty } from 'ramda';
import { match } from 'react-router-dom';
import { Grid, Header, Placeholder } from 'semantic-ui-react';
import withStyles from 'react-jss';
import classnames from 'classnames';

import { TopMetrosList, TopMetrosMap } from 'components/TopMetros';
import DownloadWidget from 'components/DownloadWidget';
import { useDatePicker } from 'components/DatePicker/DatePicker.context';
import { ClassProp } from 'helpers/types';
import {
  MetricsByMetro,
  TopMetrosByTransactionCount,
  TopMetrosByAov,
  Type as ViewDropdown,
  TopMetrosByRevenue,
} from 'components/TopMetros/TopMetros.helpers';
import {
  GET_TOP_VENUES_BY_INDICATOR,
  GET_METRICS_BY_METRO,
} from 'components/TopMetros/TopMetros.queries';
import FormSelect from 'components/FormSelect';
import { useMerchantOverview } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/MerchantOverview.context';
import Query from 'components/Query';
import { UserType } from 'components/UserMgmt/UserMgmt.context';
import NoDataOverlay from 'components/NoDataOverlay';
import NoDataImage from 'assets/icons/NoData/topmetros.svg';

import styles from './TopMetros.styles';

const viewOptions = [
  {
    key: 'transactions',
    text: 'Transactions',
    value: 'Transactions',
  },
  {
    key: 'aov',
    text: 'AOV',
    value: 'AOV',
  },
  {
    key: 'sales',
    text: 'Sales',
    value: 'Sales',
  },
];

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  match: match;
  fullWidth?: boolean;
  userType?: UserType;
  title?: string;
};

type TopMetrosMetrics = {
  transactionMetricsByMetro: MetricsByMetro[];
  topMetrosByTransactionCount: TopMetrosByTransactionCount[];
  topMetrosByAov: TopMetrosByAov[];
  topMetrosByRevenue: TopMetrosByRevenue[];
};

type TopMetrosMetricsData = {
  metricsByMetro: TopMetrosMetrics;
};

type TopVenuesByIndicator = {
  topVenuesByIndicator: MetricsByMetro[];
};

type QueryResponse = {
  data: TopMetrosMetricsData | TopVenuesByIndicator;
  loading?: boolean;
  error?: ApolloError;
};

const getTopMetros = (
  view: ViewDropdown,
  {
    topMetrosByAov,
    topMetrosByRevenue,
    topMetrosByTransactionCount,
  }: TopMetrosMetrics,
) => {
  if (view === 'AOV') {
    return topMetrosByAov;
  }

  if (view === 'Transactions') {
    return topMetrosByTransactionCount;
  }

  return topMetrosByRevenue;
};

const renderHeader = (title: string, classes: Styles): JSX.Element => (
  <Header
    className={classnames(classes.noMargin, classes.header)}
    content={title}
  />
);

const renderPlaceholder = (classes: Styles): JSX.Element => (
  <>
    <Grid.Row>
      <Grid.Column>
        <Placeholder fluid>
          <Placeholder.Line />
        </Placeholder>
      </Grid.Column>
    </Grid.Row>

    <Grid.Row>
      <Grid.Column width={9}>
        <Placeholder>
          <Placeholder.Header image>
            <Placeholder.Line />
            <Placeholder.Line />
          </Placeholder.Header>
          <Placeholder.Header image>
            <Placeholder.Line />
            <Placeholder.Line />
          </Placeholder.Header>
          <Placeholder.Header image>
            <Placeholder.Line />
            <Placeholder.Line />
          </Placeholder.Header>
          <Placeholder.Header image>
            <Placeholder.Line />
            <Placeholder.Line />
          </Placeholder.Header>
          <Placeholder.Header image>
            <Placeholder.Line />
            <Placeholder.Line />
          </Placeholder.Header>
        </Placeholder>
      </Grid.Column>

      <Grid.Column width={7}>
        <Placeholder fluid className={classes.mapPlaceholder}>
          <Placeholder.Image />
        </Placeholder>
      </Grid.Column>
    </Grid.Row>
  </>
);

const renderTopMetros = (
  classes: Styles,
  [view, setView]: [ViewDropdown, Function],
  contentRef: React.MutableRefObject<null>,
  fullWidth: boolean,
  title = 'Top Metros',
  userType?: UserType,
) => (response: QueryResponse) => {
  const tier3User = userType === 'tier3';
  let topMetros = pathOr(
    {},
    tier3User
      ? ['data', 'topVenuesByIndicator', view.toLocaleLowerCase()]
      : ['data', 'metricsByMetro'],
    response,
  );

  const { transactionMetricsByMetro } = topMetros;

  if (!Array.isArray(topMetros)) {
    topMetros = getTopMetros(view, topMetros);
  }

  const onChange = (_: SyntheticEvent<HTMLElement, Event>, { value }: any) => {
    setView(value);
  };

  return (
    <>
      <DownloadWidget
        nodeRef={contentRef}
        className={classes.download}
        options={['PNG', 'PDF']}
        imageName={'TopMetro'}
        header={
          <FormSelect
            name="view"
            value={view}
            placeholder="Transactions"
            label="View: "
            options={viewOptions}
            onChange={onChange}
          />
        }
      />

      <div ref={contentRef}>
        <Grid className={classnames(classes.container, userType)}>
          {response.loading ? (
            renderPlaceholder(classes)
          ) : (
            <>
              <Grid.Row>
                <Grid.Column textAlign="left" className={classes.formSelect}>
                  {renderHeader(`${title} (${view})`, classes)}
                </Grid.Column>
              </Grid.Row>

              {!topMetros ||
              (isEmpty(topMetros) && !transactionMetricsByMetro) ||
              isEmpty(transactionMetricsByMetro) ? (
                <Grid.Row className={classes.noDataMapRow}>
                  <NoDataOverlay noDataImage={NoDataImage} />
                </Grid.Row>
              ) : (
                <Grid.Row
                  className={classnames(classes.noPadding, classes.topMetros)}
                  verticalAlign="top"
                >
                  <Grid.Column
                    className={classes.cityList}
                    width={!transactionMetricsByMetro ? 16 : fullWidth ? 6 : 9}
                  >
                    <TopMetrosList indicator={view} topMetros={topMetros} />
                  </Grid.Column>
                  <Grid.Column
                    className={classes.map}
                    width={fullWidth ? 10 : 7}
                  >
                    <TopMetrosMap
                      metricsData={transactionMetricsByMetro}
                      height={fullWidth ? 250 : 150}
                    />
                  </Grid.Column>
                </Grid.Row>
              )}
            </>
          )}
        </Grid>
      </div>
    </>
  );
};

const TopMetros: FunctionComponent<Props> = ({
  classes,
  match,
  fullWidth = false,
  userType,
  title,
}) => {
  const { startDate, endDate } = useDatePicker();
  const { topMetroView, userProfile } = useMerchantOverview();
  const [view, setView] = useState<ViewDropdown>(topMetroView);
  const contentRef = useRef(null);
  const tier3User = userType === 'tier3';
  const merchantName = pathOr(
    userProfile.merchantName,
    ['params', 'merchantName'],
    match,
  );

  useEffect(() => {
    setView(topMetroView);
  }, [topMetroView]);

  return (
    <Query
      enableProgressInfo
      query={tier3User ? GET_TOP_VENUES_BY_INDICATOR : GET_METRICS_BY_METRO}
      render={renderTopMetros(
        classes,
        [view, setView],
        contentRef,
        fullWidth,
        title,
        userType,
      )}
      variables={{
        params: {
          startDate,
          endDate,
          merchantName,
          merchantId: userProfile.merchantId,
        },
      }}
    />
  );
};

export default withStyles(styles)(TopMetros);
